import React from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import i18n from 'i18next';
import moment from 'moment-timezone';
import { Loader } from 'semantic-ui-react';

import useAppContext from '../../../context/useAppContext';
import {
    handleSearchQuery,
    useFilters,
    useInventories,
    usePartners,
    useRates,
    useZones,
    useOriginDetails,
    useCategories,
    useContingents,
    useCrossSelling,
    useManagerContext,
    useFormFields,
    usePageSize,
    useCurrentPage,
    useSort
} from '../../manager/components/context';
import { Trans, useTranslation } from 'react-i18next';
import { useSegment, useTrackingPage } from '../../../components/tracking/trackingProvider';
import { uuid } from '../../widgets/components/pages/new-widget';

import AttendeeModel from '../../../models/attendeeModel';

import {
    Table,
    DropdownItem,
    Icon,
    Paginator,
    SelectButton,
    Card,
    ContentCard,
    WeezHeader,
    Tab,
    Button,
    FilterSetController,
    Filter,
    ButtonDropdown,
    Message,
    Dropdown,
    ToastContext,
    TooltipHelp
} from '@weezevent/nacre';
import { AttendeeRow } from './components/attendee-row/attendee-row';
import DropdownPageSize from '../../manager/components/dropdownPageSize';
import Style from './list.module.css';
import SwapSeat from '../seating/swapSeat';
import ChartedEvent from '../../../components/chartedEvent';
import SortDropdown from '../../../components/sortDropdown';
import { initAction } from '../../order/list/list';
import ActionContainer from '../../order/action-container/actionContainer';
import { ActionDropdownItem } from '../../order/action-dropdown-item/actionDropdownItem';
import SendTicketAction from '../actions/sendTicketAction';
import DeleteRefundRequestAction from '../actions/deleteRefundRequestAction';
import DeleteAction from '../actions/deleteAction';
import DownloadTicketAction from '../actions/downloadTicketActions';
import RotateBarcode from '../actions/rotateBarcode';
import PrintTicketAction from '../actions/printTicket';
import Refund from '../actions/refund';
import ChangeRate from '../actions/changeRate';
import useFirstRender from '../../../helpers/useFirstRender';
import RestoreAction from '../actions/restoreAction';
import useSeatedContext from '../../../context/seatedContext';
import SessionModel from '../../../models/sessionModel';
import Inventory from '../../../models/inventory';
import PendingAction from '../actions/moderationStatus/pendingAction';
import ValidatedAction from '../actions/moderationStatus/validatedAction';
import RejectedAction from '../actions/moderationStatus/rejectedAction';
import UncompletedAction from '../actions/moderationStatus/uncompletedAction';
import SendModerationStatusAction from '../actions/moderationStatus/sendModerationStatusAction';
import { AttendeeExportModal, OrderExportModal, ProductExportModal, GPDExportModal } from './components/export-modal';
import LoadingTable from '../../../components/loadingTable';
import { getCountriesOptions } from '../../../nacre/components/countrySelector';
import { updateContextPagination } from '../../../models/paginatedModel';

// It filters the items array to only include items whose parent property matches the current parent parameter.
// -> This gives us all items that are children of the current parent.
// It maps over these child items and for each item, it recursively calls buildTree, passing in the original items array and the uuid of the current item as the parent.
// -> This gives us an array of any grandchildren of the current parent.
// If an item has children
// -> it returns a new object that includes all properties of the current item plus a children property that is the array of grandchildren.
// If an item does not have children
// -> it is returned as is.
function buildTree(items, parent = null) {
    return items
        .filter(item => item.parent === parent)
        .map(item => {
            const children = buildTree(items, item.uuid);
            return children.length > 0 ? { ...item, children } : item;
        });
}

// transform flat array to nested tree structure
function mapZonesToOptions(items) {
    const zonesOptions = items.map(item => {
        return {
            text: item.name,
            value: item.uuid,
            parent: item.parent,
            uuid: item.uuid,
            children: []
        };
    });
    return buildTree(zonesOptions);
}
export function LabelEndDateFilter({ label, tooltipLabel }) {
    return (
        <>
            <span>{label}</span>
            <TooltipHelp triggerOnHover title={tooltipLabel} />
        </>
    );
}

export const actionList = [
    SendTicketAction,
    DownloadTicketAction,
    SendModerationStatusAction,
    RestoreAction,
    Refund,
    RotateBarcode,
    PrintTicketAction,
    PendingAction,
    ValidatedAction,
    RejectedAction,
    ChangeRate,
    UncompletedAction,
    DeleteAction,
    DeleteRefundRequestAction
];
export function ListDropdownItems({ attendee, prefix, onChangeAction, history, mode = 'list' }) {
    const { t } = useTranslation();
    const { track } = useSegment();

    return (
        <>
            <DropdownItem
                item={t('ticket.manager.attendees.action.manage_order.dropdownItem')}
                onClick={() => {
                    history.push(`${prefix}/orders/${attendee?.order?.id_transaction}`);
                    track('ACTION > MANAGE ORDER', { 'Page Title': 'ATTENDEE PROFILE' });
                }}
            />
            {actionList.map(modelAction => (
                <ActionDropdownItem key={modelAction.NAME} modelAction={modelAction} onChangeAction={onChangeAction} items={[attendee]} mode={mode} />
            ))}
        </>
    );
}

function HeaderRow({ organization, event, active = false, ordering, onChangeSort = () => {} }) {
    const { t } = useTranslation();

    return (
        <Table.Tr isHeader active={active} className={Style['tr-header']}>
            <SortDropdown
                name="order__sold_date"
                ordering={ordering}
                label={t('ticket.manager.tableHeader.soldDate')}
                items={[
                    <DropdownItem.Icons
                        key={1}
                        item={t('ticket.common.ordering.descending')}
                        checked={ordering === '-order__sold_date'}
                        onClick={() => onChangeSort('-order__sold_date')}
                    />,
                    <DropdownItem.Icons
                        key={2}
                        item={t('ticket.common.ordering.ascending')}
                        checked={ordering === 'order__sold_date'}
                        onClick={() => onChangeSort('order__sold_date')}
                    />
                ]}
            />
            <SortDropdown
                className={Style['width-large']}
                name="last_name"
                ordering={ordering}
                label={t('ticket.manager.tableHeader.attendee')}
                items={[
                    <DropdownItem.Icons key={1} item={t('ticket.common.ordering.A-Z')} checked={ordering === 'last_name'} onClick={() => onChangeSort('last_name')} />,
                    <DropdownItem.Icons key={2} item={t('ticket.common.ordering.Z-A')} checked={ordering === '-last_name'} onClick={() => onChangeSort('-last_name')} />
                ]}
            />
            {organization?.feature_flags.includes('EO_COMPANY_GDP') && <th className={`${Style['width-x-large']}`}>{t('ticket.manager.tableHeader.company')}</th>}
            <SortDropdown
                className={Style['width-large']}
                name="id_weez_ticket"
                ordering={ordering}
                label={t('ticket.manager.tableHeader.barcode')}
                items={[
                    <DropdownItem.Icons
                        key={1}
                        item={t('ticket.common.ordering.descending')}
                        checked={ordering === '-id_weez_ticket'}
                        onClick={() => onChangeSort('-id_weez_ticket')}
                    />,
                    <DropdownItem.Icons
                        key={2}
                        item={t('ticket.common.ordering.ascending')}
                        checked={ordering === 'id_weez_ticket'}
                        onClick={() => onChangeSort('id_weez_ticket')}
                    />
                ]}
            />
            <th className={`${Style['width-large']}`}>{t('ticket.manager.tableHeader.rate')}</th>
            {event.is_seated && <th className={`${Style['width-large']}`}>{t('ticket.manager.tableHeader.seat')}</th>}
            {event.use_sessions && <th className={`${Style['width-x-large']}`}>{t('ticket.manager.tableHeader.session')}</th>}
            <th className={`${Style['width-x-mini']}`}>{t('ticket.manager.tableHeader.scanned')}</th>
            <th className={`${Style['width-x-mini']}`}>{t('ticket.manager.tableHeader.status')}</th>
            {event.is_moderation_enabled && <th className={`${Style['width-regular']}`}>{t('ticket.manager.tableHeader.moderationStatus')}</th>}
            {event.is_moderation_enabled && <th className={`${Style['width-x-mini']}`}>{t('ticket.manager.tableHeader.mails')}</th>}
            <th className={`${Style['width-x-mini']}`}>{t('ticket.manager.tableHeader.actions')}</th>
        </Table.Tr>
    );
}

HeaderRow.propTypes = {
    event: PropTypes.object.isRequired,
    active: PropTypes.bool
};

export const initialFilters = {
    attendee__status__in: new Set(['active', 'reserved']),
    order__status__in: new Set(['valid', 'reservation'])
};

// Use Trans instead of t to declare and export const outside a component
// We need it for orders/list
export const optionsOrigin = [
    {
        key: 'weez_web',
        text: <Trans i18nKey="attendees.filters.ticketOriginWeb" />,
        name: 'weez_web'
    },
    {
        key: 'weez_manual_add',
        text: <Trans i18nKey="attendees.filters.ticketOriginAdded" />,
        name: 'weez_manual_add'
    },
    {
        key: 'weez_ticketoffice',
        text: <Trans i18nKey="attendees.filters.ticketOriginStand" />,
        name: 'weez_ticketoffice'
    },
    {
        key: 'weez_invitation',
        text: <Trans i18nKey="attendees.filters.ticketOriginInvitation" />,
        name: 'weez_invitation'
    },
    {
        key: 'weez_stock_print',
        text: <Trans i18nKey="attendees.filters.ticketOriginStock" />,
        name: 'weez_stock_print'
    },
    {
        key: 'weez_distributor',
        text: <Trans i18nKey="attendees.filters.ticketOriginDistributor" />,
        name: 'weez_distributor'
    }
];

const optionsStatus = [
    {
        key: 'active',
        text: <Trans i18nKey="attendees.filters.active" />,
        name: 'active'
    },
    {
        key: 'reserved',
        text: <Trans i18nKey="attendees.filters.reserved" />,
        name: 'reserved'
    },
    {
        key: 'deleted',
        text: <Trans i18nKey="attendees.filters.deleted" />,
        name: 'deleted'
    },
    {
        key: 'inactive',
        text: <Trans i18nKey="attendees.filters.inactive" />,
        name: 'inactive'
    },
    {
        key: 'unsuccessful',
        text: <Trans i18nKey="attendees.filters.unsuccessful" />,
        name: 'unsuccessful'
    }
];

export const spec = {
    // multiple is set with all multiple fields.
    // It permit to extractParameters to format value,value1 in Set(value,value1) to work with nacre filters
    multiple: [
        'booking__rate__in',
        'parent_booking__rate__in',
        'rate__in',
        'parent_rate__in',
        'parent_zone__in',
        'ticket_origin',
        'ticket_origin_detail',
        'zone__in',
        'category__in',
        'booking__contingent__in',
        'code_promo_in',
        'booking__inventory__in',
        'partner_id',
        'attendee__status__in',
        'order__status__in',
        'products__variant__in'
    ],
    // fields is the list of fields for this filters. It permit to exclude url parameters who are not useful
    // for this filter. Use to call API only
    fields: [
        'booking__rate__in',
        'rate__in',
        'parent_booking__rate__in',
        'parent_rate__in',
        'parent_zone__in',
        'ticket_origin',
        'ticket_origin_detail',
        'zone__in',
        'category__in',
        'booking__contingent__in',
        'code_promo_in',
        'booking__inventory__in',
        'partner_id',
        'attendee__status__in',
        'products__variant__in',
        'scan_status',
        'sold_date',
        'form',
        'search',
        'has_refund_requests',
        'moderation_status'
    ],
    // aliases is an object to match some parameters that came from another filter
    // Example : we can have origin__in param that came from order filters. And we want to keep this filter value
    // but in attendee case, this param is name ticket_origin
    aliases: {
        origin__in: 'ticket_origin',
        origin_detail: 'ticket_origin_detail'
    },
    date: ['sold_date'],
    form: ['form']
};

export const formatSessionDate = (isostring, timezone, locale) => {
    let dateLocalized = moment.tz(
        moment
            .utc(isostring, moment.ISO_8601)
            .tz(timezone)
            .format('YYYY-MM-DD HH:mm'),
        timezone
    );

    return new Intl.DateTimeFormat(locale, { timeStyle: 'short', dateStyle: 'short' }).format(new Date(dateLocalized));
};

export function AttendeeList() {
    const { t } = useTranslation();

    const { track } = useSegment();

    const [{ event, organization }] = useAppContext();
    const [, setManagerContext, removeFromContext] = useManagerContext();

    const history = useHistory();
    const locale = i18n.language;

    const [, { setInventory }] = useSeatedContext();

    const [pageSize, setPageSize] = usePageSize(100);
    const [currentPage, setCurrentPage] = useCurrentPage(0);
    const [ordering, setOrdering] = useSort('-order__sold_date');

    const [filters, setFilters] = useFilters(spec);
    const [rates, loadingRates, syncRates] = useRates(); // eslint-disable-line
    const [zones, loadingZones, syncZones] = useZones(); // eslint-disable-line
    const [categories, loadingCategories, syncCategories] = useCategories(); // eslint-disable-line
    const [contingents, loadingContingents, syncContingents] = useContingents(); // eslint-disable-line
    const [inventories, loadingInventories, syncInventories, requestFullListLoad] = useInventories(event.use_sessions ? SessionModel : Inventory); // eslint-disable-line
    const [partners, loadingPartners, syncPartners] = usePartners(); // eslint-disable-line
    const [formFields, loadingFormField] = useFormFields(); // eslint-disable-line
    const [crossSelling, loadingCrossSelling, syncCrossSelling] = useCrossSelling(false); // eslint-disable-line
    const [originsDetails, loadingOriginsDetails, syncOriginsDetails, optionsOriginDetails, optionsDiscount] = useOriginDetails(); // eslint-disable-line

    const [selectedAttendees, setSelectedAttendees] = React.useState({});
    const [allSelected, setAllSelected] = React.useState(false);
    const [action, setAction] = React.useState(initAction);
    const [showExportAttendeeModal, setShowExportAttendeeModal] = React.useState(false);
    const [showExportAttendeePDFModal, setShowExportAttendeePDFModal] = React.useState(false);
    const [showExportOrderModal, setShowExportOrderModal] = React.useState(false);
    const [showExportProductModal, setShowExportProductModal] = React.useState(false);
    const [showSwapSeat, setShowSwapSeat] = React.useState(false);

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization.id}/E${event.id}/manager`;
    }, [event, organization]);

    const toast = React.useContext(ToastContext);

    // We will need to know if we are in the first Render of the component to know if we have to set
    // initialFilters or if the filters are already touched
    const isFirstRender = useFirstRender();
    const filtersQuery = React.useMemo(
        () =>
            // If we are in the first render and we don't have filters already set for this tab : set InitialFilters
            isFirstRender && !Object.keys(filters).some(key => spec.fields.includes(key)) ? initialFilters : filters,
        [filters]
    );

    // Main Attendees request
    const [attendees, loading, sync, , totalCount, loadingCount] = AttendeeModel.useApiModel(
        {
            eventId: event.pk,
            organizationId: organization.pk,
            withCount: true,
            query: {
                include_deleted: true,
                include_deleted_fields: true,
                expand: ['rate.group', 'refund_requests'],
                ordering: ordering,
                limit: pageSize,
                offset: currentPage * pageSize,
                sync: false,
                ...handleSearchQuery(spec, filtersQuery, true)
            },
            onError: () => toast.error(t('ticket.manager.attendees.toast.error.retrieval_error')),
            onSuccess: res => updateContextPagination('attendees', { pageSize, ordering, currentPage }, res, setManagerContext, removeFromContext),
            onSuccessCount: res => updateContextPagination('attendees', { pageSize, ordering, currentPage }, res, setManagerContext, removeFromContext)
        },
        [pageSize, currentPage, ordering, filtersQuery, spec, setManagerContext, removeFromContext]
    );

    const onChangeSort = ordering => {
        setCurrentPage(0);
        setOrdering(ordering);
    };

    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, attendee) => {
            event.stopPropagation();
            setSelectedAttendees(currentState => {
                const newSelected = { ...currentState };
                const isSelected = attendee.pk in currentState;
                if (!checked && isSelected) {
                    delete newSelected[attendee.pk];
                }

                if (checked && !isSelected) {
                    newSelected[attendee.pk] = attendee;
                }
                setAllSelected(Object.values(newSelected).length === attendees.length);

                return newSelected;
            });
        },
        [attendees]
    );

    //@Todo problem with pagination
    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            if (checked) {
                setAllSelected(true);
                let attendeesTmp = {};
                attendees.forEach(attendee => {
                    attendeesTmp[attendee.pk] = attendee;
                });
                setSelectedAttendees(attendeesTmp);
            } else {
                setAllSelected(false);
                setSelectedAttendees({});
            }
        },
        [attendees]
    );

    const resetSelected = React.useCallback(() => {
        setAllSelected(false);
        setSelectedAttendees({});
    });

    const onCloseSwapSeat = () => {
        setSelectedAttendees({});
        setShowSwapSeat(false);
    };

    const onSubmitSwapSeat = React.useCallback(() => {
        sync();
        onCloseSwapSeat();
    }, [sync, onCloseSwapSeat]);

    const checkEditSeatingCompatibility = React.useMemo(() => {
        let _selectedAttendees = Object.values(selectedAttendees);
        if (!_selectedAttendees.length) {
            return false;
        }

        // Some attendees do not have booking
        if (_selectedAttendees.some(attendee => !attendee.booking)) {
            return false;
        }

        // Some attendees are placed in GA
        if (_selectedAttendees.some(attendee => attendee.booking.isGA)) {
            return false;
        }

        // Some attendees were invited by a partner
        if (_selectedAttendees.some(attendee => attendee.partner)) {
            return false;
        }

        let unicity = _selectedAttendees.reduce(
            (acc, attendee) => {
                let booking = attendee.booking;
                acc.contingents.push(booking.contingent);
                acc.inventories.push(booking.inventory);
                return acc;
            },
            {
                contingents: [],
                categories: [],
                inventories: []
            }
        );

        let contingentsTmp = new Set(unicity.contingents);
        let inventoriesTmp = new Set(unicity.inventories);

        //  Same contingent and Same inventory
        if (contingentsTmp.size > 1 || inventoriesTmp.size > 1) {
            return false;
        }

        // We must set the inventory in the seatedContext, to be able to place according to the correct availability and therefore the correct inventory
        if (inventoriesTmp.size === 1) {
            setInventory(inventories.find(i => i.uuid === Array.from(inventoriesTmp)[0]));
        }

        return true;
    }, [selectedAttendees]);

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );
    const optionsRates = React.useMemo(() => {
        if (!rates || Array.isArray(rates)) {
            return [];
        }
        return rates.toOptions({
            extraRate: rate => ({
                value: String(rate.id),
                classname: 'row-rate',
                text: rate.name || rate.rate_template?.name,
                parent: rate.group ? String(rate.group) : null
            }),

            extraGroup: group => ({
                classname: group.parent ? 'row-sub-group' : 'row-group',
                value: String(group.id),
                text: group.name || group.group_template?.name,
                parent: group.parent ? String(group.parent) : null
            })
        });
    }, [rates, locale]);

    const optionsZones = React.useMemo(() => {
        if (!zones || zones.length === 0) {
            return [];
        }
        return mapZonesToOptions(zones);
    }, [zones]);

    const optionsCategories = React.useMemo(() => {
        return categories?.map(category => ({
            text: category.name,
            name: category.uuid,
            key: category.uuid
        }));
    }, [categories]);

    const optionsContingents = React.useMemo(() => {
        return contingents?.map(contingent => ({
            text: contingent.name,
            name: contingent.uuid,
            key: contingent.uuid
        }));
    }, [contingents]);

    const optionsInventories = React.useMemo(() => {
        if (event.use_sessions) {
            return SessionModel.toOptions({
                sessions: inventories,
                timezone: event.timezone,
                locale
            });
        }
        return event.inventories?.map(inventory => ({
            text: inventory.name,
            name: inventory.uuid,
            key: inventory.uuid
        }));
    }, [event, inventories, locale]);

    const optionsPartners = React.useMemo(() => {
        return partners?.map(partner => ({
            text: partner.name,
            name: partner.uuid,
            key: partner.uuid
        }));
    }, [partners]);

    const formFieldOptions = React.useMemo(() => {
        if (!formFields || !formFields.length) {
            return null;
        }
        return formFields
            ?.filter(field => !['address_autocomplete', 'latitude_longitude', 'divider'].includes(field.type))
            .filter(f => f.weez_id !== 'ws-36')
            .map(field => {
                if (field.type === 'select') {
                    return {
                        weez_id: field.weez_id,
                        label: field.label,
                        type: field.type,
                        usage: field.usage,
                        choices: field.choices.map(choice => {
                            return {
                                key: choice.weez_id,
                                text: choice.label,
                                value: choice.label
                            };
                        })
                    };
                }
                return field;
            });
    }, [formFields]);

    const formFieldLabels = React.useMemo(() => {
        return {
            labelBack: t('ticket.manager.filters.form.back'),
            labelName: t('ticket.common.form.form'),
            labelOperator: t('ticket.manager.filters.form.label_operator'),
            labelInput: t('ticket.manager.filters.form.label_input'),
            sectionLabels: {
                attendee: t('ticket.common.attendee'),
                buyer: t('ticket.common.buyer')
            },
            operatorsLabels: {
                gt: t('ticket.manager.filters.form.more_than'),
                lt: t('ticket.manager.filters.form.less_than'),
                gte: t('ticket.manager.filters.date.before'),
                lte: t('ticket.manager.filters.date.after'),
                range: t('ticket.manager.filters.date.between'),
                equal: t('ticket.manager.filters.form.equal'),
                startswith: t('ticket.manager.filters.form.starts_with'),
                isnull: t('ticket.manager.filters.form.is_null'),
                is_different: t('ticket.manager.filters.form.is_different')
            },
            labelFields: t('ticket.manager.filters.form.label_fields'),
            labelOptionsSelect: {
                checked: t('ticket.manager.filters.form.checked'),
                not_checked: t('ticket.manager.filters.form.not_checked'),
                filled: t('ticket.manager.filters.form.filled'),
                not_filled: t('ticket.manager.filters.form.not_filled')
            },
            labelCTA: t('ticket.common.action.apply'),
            labelDate: t('ticket.manager.filters.form.date'),
            labelHour: t('ticket.manager.filters.form.hour'),
            labelStartDate: t('ticket.manager.filters.form.start_date'),
            labelStartHour: t('ticket.manager.filters.form.start_hour'),
            labelEndDate: t('ticket.manager.filters.form.end_date'),
            labelEndHour: t('ticket.manager.filters.form.end_hour')
        };
    }, [t]);

    const countriesOptions = React.useMemo(() => {
        return getCountriesOptions(locale);
    }, [locale]);

    const optionsCrossSelling = React.useMemo(() => {
        let variants = [];
        crossSelling?.map(product => {
            product.variants.forEach(variant => {
                variants.push({
                    text: `${product.name} - ${variant.name}`,
                    name: variant.id.toString(),
                    key: variant.id.toString()
                });
            });
        });
        return variants;
    }, [crossSelling]);

    const optionsScan = [
        {
            key: 'scanned',
            text: t('attendees.filters.scanStatusScanned'),
            name: 'True'
        },
        {
            key: 'not_scanned',
            text: t('attendees.filters.scanStatusNotScanned'),
            name: 'False'
        }
    ];

    const optionsRefundRequest = [
        {
            key: 'has_refund_request',
            text: t('attendees.filters.hasRefundRequest'),
            name: 'True'
        },
        {
            key: 'has_not_refund_request',
            text: t('attendees.filters.hasNotRefundRequest'),
            name: 'False'
        }
    ];

    const optionsModerationStatus = [
        {
            key: 'pending',
            text: t('ticket.manager.common.moderation.pending'),
            name: 'pending'
        },
        {
            key: 'validated',
            text: t('ticket.manager.common.moderation.validated'),
            name: 'validated'
        },
        {
            key: 'rejected',
            text: t('ticket.manager.common.moderation.rejected'),
            name: 'rejected'
        },
        {
            key: 'uncomplete',
            text: t('ticket.manager.common.moderation.uncomplete'),
            name: 'uncomplete'
        }
    ];

    const isLoadingFilters = React.useMemo(() => {
        const loadingCondition = [loadingRates, loadingFormField, loadingCrossSelling, loadingOriginsDetails, loadingPartners];

        if (event.is_seated) {
            loadingCondition.push(...[loadingZones, loadingCategories, loadingContingents, loadingInventories]);
        }
        if (event.use_sessions) {
            loadingCondition.push(loadingInventories);
        }
        return loadingCondition.some(loading => loading);
    }, [
        event,
        loadingRates,
        loadingZones,
        loadingCategories,
        loadingContingents,
        loadingInventories,
        loadingPartners,
        loadingFormField,
        loadingCrossSelling,
        loadingOriginsDetails
    ]);

    const onCloseAction = React.useCallback(() => {
        setAction(initAction);
        resetSelected();
    }, [resetSelected]);

    const handleTrackFilters = React.useCallback(
        ({ action }) => {
            track(`ACTION > ${action}`, { 'Page Title': 'MANAGE ATTENDEES' });
        },
        [track]
    );

    useTrackingPage('VIEW - MANAGE ATTENDEES', { 'Page Title': 'MANAGE ATTENDEES' });

    return (
        <>
            <AttendeeExportModal
                organization={organization}
                event={event}
                showExportModal={showExportAttendeeModal}
                setShowExportModal={setShowExportAttendeeModal}
                from="attendeeList"
                context="attendees"
                ordering={ordering}
                selected={selectedAttendees}
                filtersQs={Object.entries(handleSearchQuery(spec, filters))
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&')}
            />
            <OrderExportModal
                organization={organization}
                event={event}
                showExportModal={showExportOrderModal}
                setShowExportModal={setShowExportOrderModal}
                from="attendeeList"
                ordering={ordering}
                selected={selectedAttendees}
                filtersQs={Object.entries(handleSearchQuery(spec, filters))
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&')}
            />
            <ProductExportModal organization={organization} event={event} showExportModal={showExportProductModal} setShowExportModal={setShowExportProductModal} />
            <GPDExportModal
                organization={organization}
                event={event}
                eventIds={[event.id]}
                from="attendeeList"
                context="attendees_pdf"
                selected={selectedAttendees}
                showExportModal={showExportAttendeePDFModal}
                setShowExportModal={setShowExportAttendeePDFModal}
                filtersQs={Object.entries(handleSearchQuery(spec, filters))
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&')}
                ordering={ordering}
            />
            <WeezHeader
                title={t('ticket.manager.title')}
                rightComponent={[
                    <Button
                        primary
                        key="top-cta-form"
                        icon={<Icon name={'plus'} margin={false} />}
                        label={t('ticket.manager.addAttendees')}
                        onClick={() => {
                            track('ACTION > ADD ATTENDEES', { 'Page Title': 'MANAGE ATTENDEES' });
                            history.push(`/ticket/O${organization.id}/E${event.id}/attendees/add`);
                        }}
                    />,
                    <ButtonDropdown
                        key={'dropdown'}
                        onOpen={() => track('ACTION > ACTION MANAGE ATTENDEES', { 'Page Title': 'MANAGE ATTENDEES' })}
                        renderWhenClosed
                        trigger={<Button inverted primary icon={<Icon name="chevron-bottom" />} label={t('ticket.common.cta.actions')} />}
                        items={[
                            <Dropdown.Item
                                key="export_attendees"
                                onClick={() => {
                                    track('ACTION > EXPORT ATTENDEE LIST', { 'Page Title': 'MANAGE ATTENDEES' });
                                    setShowExportAttendeeModal(true);
                                }}
                                item={t('ticket.manager.attendees.action.export.button')}
                            />,
                            <Dropdown.Item
                                key="export_orders"
                                onClick={() => {
                                    track('ACTION > EXPORT ORDER LIST', { 'Page Title': 'MANAGE ATTENDEES' });
                                    setShowExportOrderModal(true);
                                }}
                                item={t('ticket.manager.orders.action.export.button')}
                            />,
                            <Dropdown.Item
                                key="export_products"
                                onClick={() => {
                                    track('ACTION > EXPORT PRODUCT LIST', { 'Page Title': 'MANAGE ATTENDEES' });
                                    setShowExportProductModal(true);
                                }}
                                item={t('ticket.manager.products.action.export.title')}
                            />,
                            <Dropdown.Item
                                key="export_attendees_pdf"
                                onClick={() => {
                                    track('ACTION > ATTENDEES PDF REPORT', { 'Page Title': 'MANAGE ATTENDEES' });
                                    setShowExportAttendeePDFModal(true);
                                }}
                                item={t('ticket.export.download_export_attendees_pdf')}
                            />
                        ]}
                    />
                ]}
            >
                {t('ticket.manager.subtitle')}
            </WeezHeader>
            <ChartedEvent event={event}>
                <Message title={t('ticket.manager.attendees.messageViewMapTitle')} type="default">
                    <span>
                        <Link
                            className={Style['link-view-map']}
                            to={`/ticket/O${organization.pk}/E${event.pk}/seating/attendees/manage?display=map`}
                            onClick={() => {
                                track('GDP - Access Seatmap View', {
                                    'Page Title': 'GDP'
                                });
                            }}
                        >
                            {t('ticket.common.clickHere')}
                        </Link>
                        {` ${t('ticket.manager.attendees.messageViewMapContent')}`}
                    </span>
                </Message>
            </ChartedEvent>
            <Tab
                panes={[
                    {
                        menuItem: t('ticket.manager.attendees.titleTab'),
                        menuIcon: 'user',
                        url: `${prefix}/attendees`,
                        key: 'attendees'
                    },
                    {
                        menuItem: t('ticket.manager.orders.titleTab'),
                        menuIcon: 'package',
                        url: `${prefix}/orders`,
                        key: 'orders'
                    }
                ]}
                activeTab={0}
                onTabSelected={tab => {
                    if (tab.key === 'orders') {
                        track('ACTION > TAB MANAGE ORDERS', { 'Page Title': 'MANAGE ATTENDEES' });
                    }

                    //reset pagination when we click on tab
                    setManagerContext(ctx => {
                        delete ctx['page'];
                        delete ctx['size'];
                        delete ctx['sort'];
                        return { ...ctx };
                    });
                    history.push(tab.url);
                }}
            >
                <ActionContainer mode={'attendees'} sync={() => sync({ allow_cache: false, cache: false })} onClose={onCloseAction} {...action} />
                <SwapSeat key={uuid()} show={showSwapSeat} onClose={onCloseSwapSeat} onSubmit={onSubmitSwapSeat} attendees={selectedAttendees} />
                <Card className={Style['list-container']}>
                    <ContentCard>
                        <FilterSetController
                            total={totalCount}
                            isLoadingTotal={loading || loadingCount}
                            labelTotal={t('ticket.common.attendee', { count: totalCount })}
                            labelReset={t('ticket.common.action.reset')}
                            labelFilters={t('ticket.common.cta.filters')}
                            isLoading={isLoadingFilters}
                            onOpen={() => {
                                track('ACTION > FILTER', { 'Page Title': 'MANAGE ATTENDEES' });
                                requestFullListLoad();
                            }}
                            search={{
                                onChange: (synth, value) => {
                                    onChangeSearch(value);
                                    resetSelected();
                                },
                                handleReset: (synth, value) => {
                                    onChangeSearch(null);
                                },
                                placeholder: t('ticket.common.action.search'),
                                value: filters?.search || ''
                            }}
                            onChange={values => {
                                if (filters.search !== undefined && filters.search.length > 0) {
                                    values.search = filters.search;
                                } else {
                                    delete values.search;
                                }
                                setFilters(values);
                                resetSelected();
                                // At the first render we don't want to set the page to 0, because a page can be specified in the url.
                                // Otherwise each time we change the filter, we reset the page to 0
                                if (!isFirstRender) {
                                    setCurrentPage(0);
                                }
                            }}
                            // use Initial Values to set filters Values form initialFilter or filters that are coming
                            // from another tab
                            initialValues={() => {
                                // If we are in the first render, we want to set initialFilters for this tab
                                // We check if there's already a filter selected for this tab and if not we merge
                                // filters to keep values from other tab with initialFilters
                                if (isFirstRender && !Object.keys(filters).some(key => spec.fields.includes(key))) {
                                    return { ...filters, ...initialFilters };
                                    // else we just check if we have filters already set, otherwise we set initialFilters
                                } else {
                                    return Object.keys(filters).length > 0 ? filters : initialFilters;
                                }
                            }}
                            // default Values is set only for reset function. It will reset to initialFilters and not
                            // with all the filters selected through other tabs
                            defaultValues={initialFilters}
                            onReset={() => {}}
                        >
                            {optionsRates.length > 0 && (
                                <Filter
                                    name={event.is_seated ? 'booking__rate__in' : 'rate__in'}
                                    labelName={t('ticket.common.rates')}
                                    hierarchy
                                    searchable={rates?.rates_list?.length > 7}
                                    placeholderSearch={t('ticket.common.search')}
                                    options={optionsRates}
                                    index="1"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER RATES' })}
                                />
                            )}
                            <Filter
                                name="scan_status"
                                labelName={t('attendees.filters.scanStatus')}
                                options={optionsScan}
                                index="2"
                                onPostChange={() => handleTrackFilters({ action: 'FILTER STATUS SCAN' })}
                            />
                            <Filter // Not functional
                                name="ticket_origin"
                                labelName={t('ticket.common.origin')}
                                multiple
                                options={optionsOrigin}
                                index="3"
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelCTA={t('ticket.common.action.apply')}
                                onPostChange={() => handleTrackFilters({ action: 'FILTER ORIGIN' })}
                            />
                            <Filter
                                name="has_refund_requests"
                                labelName={t('attendees.filters.has_refund_requests')}
                                options={optionsRefundRequest}
                                index="14"
                                onPostChange={() => handleTrackFilters({ action: 'FILTER REFUND REQUESTS' })}
                            />

                            {optionsOriginDetails.length > 0 && (
                                <Filter
                                    name="ticket_origin_detail"
                                    labelName={t('ticket.common.distributor')}
                                    multiple
                                    options={optionsOriginDetails}
                                    index="4"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER ORIGIN DETAIL' })}
                                />
                            )}
                            {event.is_seated && zones?.length > 0 && (
                                <Filter
                                    name="zone__in"
                                    labelName={t('ticket.common.seating.zones')}
                                    hierarchy
                                    useTreeLogic={false}
                                    options={optionsZones}
                                    index="5"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER ZONE' })}
                                />
                            )}
                            {((event.is_seated && event.inventories?.length > 0) || (event.use_sessions && inventories?.length > 0)) && (
                                <Filter
                                    component={
                                        loadingInventories
                                            ? () => (
                                                  <div style={{ height: 150 }}>
                                                      <Loader active />
                                                  </div>
                                              )
                                            : null
                                    }
                                    hierarchy={event.use_sessions}
                                    selectableGroup={false}
                                    name="booking__inventory__in"
                                    labelName={event.use_sessions ? t('ticket.common.sessions') : t('ticket.common.inventories')}
                                    multiple={!event.use_sessions}
                                    options={optionsInventories}
                                    index="6"
                                    searchable // It's for the sessions case, we use the search in the Hierarchy component
                                    isSearchable // It's for the inventories case we can use the search in filter directly
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER INVENTORY' })}
                                />
                            )}
                            {partners.length > 0 && (
                                <Filter
                                    name="partner_id"
                                    labelName={t('attendees.filters.partner')}
                                    multiple
                                    options={optionsPartners}
                                    index="7"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER PARTNERS' })}
                                />
                            )}
                            <Filter
                                date
                                index="8"
                                name="sold_date"
                                locale={locale}
                                timeStep={5}
                                timezone={event.timezone}
                                labelCTA={t('ticket.common.action.apply')}
                                labelName={t('ticket.manager.filters.order_date')}
                                labelAfter={t('ticket.manager.filters.date.after')}
                                labelBefore={t('ticket.manager.filters.date.before')}
                                labelBetween={t('ticket.manager.filters.date.between')}
                                labelSelect={t('ticket.manager.filters.date.select')}
                                labelDate={t('ticket.manager.filters.date.date')}
                                labelHour={t('ticket.manager.filters.date.hour')}
                                labelStartDate={t('ticket.manager.filters.date.start_date')}
                                labelStartHour={t('ticket.manager.filters.date.start_hour')}
                                labelEndDate={
                                    <LabelEndDateFilter label={t('ticket.manager.filters.date.end_date')} tooltipLabel={t('ticket.manager.filters.order_date_tooltip')} />
                                }
                                labelEndHour={t('ticket.manager.filters.date.end_hour')}
                                customLabelEndDate={
                                    <LabelEndDateFilter label={t('ticket.manager.filters.date.end_date')} tooltipLabel={t('ticket.manager.filters.order_date_tooltip')} />
                                }
                                onPostChange={() => handleTrackFilters({ action: 'FILTER ORDER DATE' })}
                            />
                            <Filter
                                name="attendee__status__in"
                                labelName={t('attendees.filters.status')}
                                multiple
                                options={optionsStatus}
                                index="9"
                                labelSelectAll={t('ticket.common.action.select-all')}
                                labelCTA={t('ticket.common.action.apply')}
                                onPostChange={() => handleTrackFilters({ action: 'FILTER STATUS' })}
                            />
                            {event.is_seated && categories?.length > 0 && (
                                <Filter
                                    name="category__in"
                                    labelName={t('attendees.filters.category')}
                                    multiple
                                    options={optionsCategories}
                                    index="10"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER CATEGORY' })}
                                />
                            )}
                            {event.is_seated && contingents?.length > 0 && (
                                <Filter
                                    name="booking__contingent__in"
                                    labelName={t('attendees.filters.contingent')}
                                    multiple
                                    options={optionsContingents}
                                    index="11"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER CONTINGENT' })}
                                />
                            )}
                            {optionsDiscount.length > 0 && (
                                <Filter
                                    name="code_promo_in"
                                    labelName={t('attendees.filters.discount')}
                                    multiple
                                    options={optionsDiscount}
                                    index="12"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER DISCOUNT CODE' })}
                                />
                            )}
                            {optionsCrossSelling.length > 0 && (
                                <Filter
                                    name="products__variant__in"
                                    labelName={t('ticket.common.cross_selling')}
                                    multiple
                                    options={optionsCrossSelling}
                                    index="13"
                                    labelSelectAll={t('ticket.common.action.select-all')}
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER CROSS SELLING' })}
                                />
                            )}
                            {event.is_moderation_enabled && (
                                <Filter
                                    name="moderation_status"
                                    labelName={t('ticket.common.moderationStatus')}
                                    options={optionsModerationStatus}
                                    index="15"
                                    labelCTA={t('ticket.common.action.apply')}
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER MODERATION STATUS' })}
                                />
                            )}
                            {formFieldOptions?.length > 0 && (
                                <Filter
                                    {...formFieldLabels}
                                    name="form"
                                    form
                                    options={formFieldOptions}
                                    countriesOptions={countriesOptions}
                                    index="16"
                                    onPostChange={() => handleTrackFilters({ action: 'FILTER FORM' })}
                                />
                            )}
                        </FilterSetController>
                        <div className={Style['global-pagesize-container']}>
                            <SelectButton
                                tooltipTitle={t('ticket.manager.attendees.noActionTooltip')}
                                onOpen={() => track('ACTION > ACTION SELECTION', { 'Page Title': 'MANAGE ATTENDEES' })}
                                title={
                                    Object.values(selectedAttendees).length > 0
                                        ? t('ticket.manager.attendees.attendeeAction', { count: Object.values(selectedAttendees).length })
                                        : t('ticket.common.action.bulk')
                                }
                                objects={Object.values(selectedAttendees)}
                            >
                                {event.isRealSeatingNoSessions() && (
                                    <DropdownItem disabled={!checkEditSeatingCompatibility} item={t('ticket.common.cta.swap')} onClick={() => setShowSwapSeat(true)} />
                                )}
                                {actionList.map(modelAction => (
                                    <ActionDropdownItem
                                        key={modelAction.NAME}
                                        modelAction={modelAction}
                                        onChangeAction={setAction}
                                        items={Object.values(selectedAttendees)}
                                        type="multiple"
                                    />
                                ))}
                            </SelectButton>
                            <DropdownPageSize
                                loading={loading}
                                current={pageSize}
                                onClick={() => track('ACTION > SEE MORE ATTENDEES', { 'Page Title': 'MANAGE ATTENDEES' })}
                                onChange={setPageSize}
                                pageSizes={[10, 25, 50, 100, 250]}
                                onChangeCurrentpage={setCurrentPage}
                                totalCount={totalCount || 0}
                            />
                        </div>
                    </ContentCard>
                    <Table useNew selectable onSelectAll={(event, data) => handleAllClick({ event, data })} className={Style['list__table']}>
                        <Table.THead>
                            <HeaderRow organization={organization} event={event} active={allSelected} ordering={ordering} onChangeSort={onChangeSort} />
                        </Table.THead>
                        {!loading && attendees && (
                            <Table.TBody>
                                {attendees?.map((attendee, index) => (
                                    <AttendeeRow
                                        key={attendee.pk}
                                        index={index}
                                        attendee={attendee}
                                        isSeatingEvent={event.is_seated}
                                        isChecked={Boolean(selectedAttendees[attendee.pk])}
                                        onSelected={handleElementsList}
                                        onChangeAction={setAction}
                                        onChangeShowEditSeating={setShowSwapSeat}
                                        sync={() => sync({ allow_cache: false, cache: false })}
                                    />
                                ))}
                            </Table.TBody>
                        )}
                    </Table>
                    <LoadingTable loading={loading} />
                    {!loading && attendees && !attendees?.length && totalCount !== '-' && (
                        <Card.EmptyState
                            title={t('ticket.manager.attendees.noAttendees')}
                            subtitle={t('ticket.manager.attendees.noAttendeesSubtitle')}
                            icon={<Icon name="user" />}
                        />
                    )}
                    <div style={{ textAlign: 'center', margin: '10px 0' }}>
                        <Paginator
                            currentPage={currentPage}
                            onPageChange={numberPage => {
                                setCurrentPage(numberPage);
                                resetSelected();
                            }}
                            totalCount={totalCount !== '-' ? +totalCount : 0}
                            pageSize={pageSize}
                        />
                    </div>
                </Card>
            </Tab>
        </>
    );
}
